import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail,
  Send,
  Users,
  Filter,
  RefreshCw,
  Copy,
  Check,
  ChevronDown,
  AlertCircle,
  Clock,
  Package,
  CreditCard,
  Shield,
  TrendingDown,
  Search,
  X
} from 'lucide-react';
import { useToast } from './ui/Toast';
import './MerchantNudges.css';

// WhatsApp brand icon
const WhatsAppIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// AI-generated nudge templates
const nudgeTemplates = {
  inactive: [
    "Hi {{merchantName}}! We noticed you haven't been active on Revstar lately. Your store {{storeName}} is waiting for you! Check it out: {{storeUrl}} and log in to your dashboard to start selling again: {{dashboardUrl}}",
    "Hey {{merchantName}}, it's been a while! Your customers might be missing you. Your store is live at {{storeUrl}} - log in at {{dashboardUrl}} and start selling again!",
    "{{merchantName}}, your store {{storeName}} has been quiet. Your storefront is ready at {{storeUrl}}. Log in to manage it: {{dashboardUrl}}"
  ],
  noProducts: [
    "Hi {{merchantName}}! Your store {{storeName}} is all set up but has no products yet. Add your first product today and start selling: {{dashboardUrl}}/products/new",
    "Hey {{merchantName}}, ready to make your first sale? Add products to {{storeName}} now - it only takes a few minutes: {{dashboardUrl}}/products",
    "{{merchantName}}, your storefront is live but empty! Fill {{storeName}} with amazing products and watch the orders come in: {{dashboardUrl}}/products"
  ],
  incompleteKYC: [
    "Hi {{merchantName}}, complete your KYC verification to unlock higher transaction limits and start receiving payments on {{storeName}}: {{dashboardUrl}}/settings/kyc",
    "{{merchantName}}, you're almost there! Finish your KYC verification to fully activate your store {{storeName}}: {{dashboardUrl}}/settings/kyc",
    "Hey {{merchantName}}, your business verification is pending. Complete it now to start accepting payments on {{storeName}}: {{dashboardUrl}}/settings/kyc"
  ],
  noPaymentGateway: [
    "Hi {{merchantName}}! Enable payment methods on {{storeName}} to start accepting online payments from customers: {{dashboardUrl}}/settings?tab=payments",
    "{{merchantName}}, you're missing out! Set up payment gateways on {{storeName}} and start receiving payments today: {{dashboardUrl}}/settings?tab=payments",
    "Hey {{merchantName}}, your store {{storeName}} is ready but can't accept payments yet. Enable payment methods now: {{dashboardUrl}}/settings?tab=payments"
  ],
  lowSales: [
    "Hi {{merchantName}}, want to boost sales on {{storeName}}? Share your store link: {{storeUrl}} with friends and on social media!",
    "{{merchantName}}, let's grow {{storeName}} together! Share your store {{storeUrl}} on WhatsApp and Instagram to reach more customers.",
    "Hey {{merchantName}}, your store {{storeName}} has potential! Share {{storeUrl}} to attract more customers."
  ]
};

const MerchantNudges = () => {
  const toast = useToast();
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMerchants, setSelectedMerchants] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [nudgeType, setNudgeType] = useState('inactive');
  const [customMessage, setCustomMessage] = useState('');
  const [selectedChannel, setSelectedChannel] = useState('whatsapp');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  // Auto-detect platform domain from admin hostname or use env variable
  const getBaseDomain = () => {
    const envDomain = process.env.REACT_APP_PLATFORM_DOMAIN;
    if (envDomain) return envDomain;

    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'localhost:3000';
    }
    // Strip admin./console. prefix to get base domain
    return hostname.replace(/^(admin|console)\./, '');
  };

  const baseDomain = getBaseDomain();
  const isLocalhost = baseDomain.includes('localhost');
  const protocol = isLocalhost ? 'http' : 'https';
  // Dashboard URL: for localhost use direct URL, for production use app. subdomain
  const dashboardUrl = isLocalhost ? `${protocol}://${baseDomain}` : `${protocol}://app.${baseDomain}`;

  const fetchMerchants = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/merchants/nudge-candidates', {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setMerchants(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching merchants:', error);
      toast.error('Failed to fetch merchants');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchMerchants();
  }, [fetchMerchants]);

  const getNudgeReason = (merchant) => {
    if (!merchant.lastLoginAt || daysSince(merchant.lastLoginAt) > 14) return 'inactive';
    if (!merchant.productCount || merchant.productCount === 0) return 'noProducts';
    if (!merchant.kycStatus || merchant.kycStatus === 'NOT_STARTED') return 'incompleteKYC';
    if (!merchant.hasActiveGateway) return 'noPaymentGateway';
    if (merchant.orderCount < 5 && merchant.productCount > 0) return 'lowSales';
    return null;
  };

  const daysSince = (dateStr) => {
    if (!dateStr) return 999;
    const date = new Date(dateStr);
    const now = new Date();
    return Math.floor((now - date) / (1000 * 60 * 60 * 24));
  };

  const getRandomTemplate = (type) => {
    const templates = nudgeTemplates[type] || nudgeTemplates.inactive;
    return templates[Math.floor(Math.random() * templates.length)];
  };

  const processTemplate = (template, merchant) => {
    // Build store URL: custom domain > subdomain > dashboard fallback
    console.log('[Nudge UI] Processing merchant:', merchant.email, 'storeSlug:', merchant.storeSlug, 'customDomain:', merchant.customDomain);
    let storeUrl;
    if (merchant.customDomain) {
      storeUrl = `https://${merchant.customDomain}`;
    } else if (merchant.storeSlug) {
      storeUrl = `${protocol}://${merchant.storeSlug}.${baseDomain}`;
    } else {
      storeUrl = dashboardUrl;
    }
    console.log('[Nudge UI] Built storeUrl:', storeUrl);

    return template
      .replace(/\{\{merchantName\}\}/g, merchant.firstName || merchant.username || 'there')
      .replace(/\{\{storeName\}\}/g, merchant.storeName || 'your store')
      .replace(/\{\{storeUrl\}\}/g, storeUrl)
      .replace(/\{\{dashboardUrl\}\}/g, dashboardUrl);
  };

  const filteredMerchants = merchants.filter(m => {
    const reason = getNudgeReason(m);
    if (filter !== 'all' && reason !== filter) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        m.email?.toLowerCase().includes(search) ||
        m.firstName?.toLowerCase().includes(search) ||
        m.lastName?.toLowerCase().includes(search) ||
        m.storeName?.toLowerCase().includes(search)
      );
    }
    return reason !== null;
  });

  const handleSelectAll = () => {
    if (selectedMerchants.length === filteredMerchants.length) {
      setSelectedMerchants([]);
    } else {
      setSelectedMerchants(filteredMerchants.map(m => m.id));
    }
  };

  const handleSelectMerchant = (merchantId) => {
    setSelectedMerchants(prev =>
      prev.includes(merchantId)
        ? prev.filter(id => id !== merchantId)
        : [...prev, merchantId]
    );
  };

  const openComposeModal = (merchantIdOverride = null) => {
    const merchantIds = merchantIdOverride ? [merchantIdOverride] : selectedMerchants;
    if (merchantIds.length === 0) {
      toast.error('Please select at least one merchant');
      return;
    }
    // Update selected merchants if override provided
    if (merchantIdOverride) {
      setSelectedMerchants([merchantIdOverride]);
    }
    // Generate initial message based on first selected merchant
    const firstMerchant = merchants.find(m => m.id === merchantIds[0]);
    if (!firstMerchant) {
      toast.error('Merchant not found');
      return;
    }
    const reason = getNudgeReason(firstMerchant) || 'inactive';
    setNudgeType(reason);
    const template = getRandomTemplate(reason);
    setCustomMessage(processTemplate(template, firstMerchant));
    setShowComposeModal(true);
  };

  const regenerateMessage = () => {
    const firstMerchant = merchants.find(m => m.id === selectedMerchants[0]);
    const template = getRandomTemplate(nudgeType);
    setCustomMessage(processTemplate(template, firstMerchant));
  };

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(customMessage);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Message copied!');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const sendNudge = async () => {
    setSending(true);
    try {
      const response = await fetch('/api/admin/merchants/send-nudge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          merchantIds: selectedMerchants,
          channel: selectedChannel,
          message: customMessage,
          nudgeType
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Nudge sent to ${selectedMerchants.length} merchant(s)`);
        setShowComposeModal(false);
        setSelectedMerchants([]);
      } else {
        toast.error(data.error || 'Failed to send nudge');
      }
    } catch (error) {
      console.error('Error sending nudge:', error);
      toast.error('Failed to send nudge');
    } finally {
      setSending(false);
    }
  };

  // Format phone number with country code for WhatsApp
  const formatPhoneForWhatsApp = (phone) => {
    if (!phone) return null;
    // Remove all non-digits
    let cleaned = phone.replace(/\D/g, '');

    // Handle Nigerian numbers (most common case)
    // If starts with 0, replace with 234 (Nigeria country code)
    if (cleaned.startsWith('0') && cleaned.length === 11) {
      cleaned = '234' + cleaned.substring(1);
    }
    // If it's a short number without country code, assume Nigeria
    else if (cleaned.length === 10 && !cleaned.startsWith('234')) {
      cleaned = '234' + cleaned;
    }
    // If already has country code (starts with 234, 1, 44, etc.), use as is

    return cleaned;
  };

  const openWhatsAppForMerchant = (merchant) => {
    const reason = getNudgeReason(merchant) || 'inactive';
    const template = getRandomTemplate(reason);
    const message = processTemplate(template, merchant);
    const phone = formatPhoneForWhatsApp(merchant.phone);
    if (phone) {
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
    } else {
      toast.error('Merchant has no phone number');
    }
  };

  const getReasonIcon = (reason) => {
    switch (reason) {
      case 'inactive': return <Clock size={14} />;
      case 'noProducts': return <Package size={14} />;
      case 'incompleteKYC': return <Shield size={14} />;
      case 'noPaymentGateway': return <CreditCard size={14} />;
      case 'lowSales': return <TrendingDown size={14} />;
      default: return <AlertCircle size={14} />;
    }
  };

  const getReasonLabel = (reason) => {
    switch (reason) {
      case 'inactive': return 'Inactive';
      case 'noProducts': return 'No Products';
      case 'incompleteKYC': return 'KYC Incomplete';
      case 'noPaymentGateway': return 'No Payments';
      case 'lowSales': return 'Low Sales';
      default: return 'Needs Attention';
    }
  };

  const getReasonColor = (reason) => {
    switch (reason) {
      case 'inactive': return '#6b7280';
      case 'noProducts': return '#f59e0b';
      case 'incompleteKYC': return '#3b82f6';
      case 'noPaymentGateway': return '#ef4444';
      case 'lowSales': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  return (
    <div className="merchant-nudges-container">
      <div className="nudges-page-header">
        <div>
          <h1>Merchant Engagement</h1>
          <p>Send targeted nudges to help merchants succeed</p>
        </div>
        <button className="refresh-btn" onClick={fetchMerchants} disabled={loading}>
          <RefreshCw size={18} className={loading ? 'spinning' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="nudges-stats-grid">
        <div className="nudges-stat-card">
          <Users size={20} />
          <div>
            <span className="stat-value">{merchants.length}</span>
            <span className="stat-label">Need Attention</span>
          </div>
        </div>
        <div className="nudges-stat-card inactive">
          <Clock size={20} />
          <div>
            <span className="stat-value">{merchants.filter(m => getNudgeReason(m) === 'inactive').length}</span>
            <span className="stat-label">Inactive</span>
          </div>
        </div>
        <div className="nudges-stat-card no-products">
          <Package size={20} />
          <div>
            <span className="stat-value">{merchants.filter(m => getNudgeReason(m) === 'noProducts').length}</span>
            <span className="stat-label">No Products</span>
          </div>
        </div>
        <div className="nudges-stat-card no-kyc">
          <Shield size={20} />
          <div>
            <span className="stat-value">{merchants.filter(m => getNudgeReason(m) === 'incompleteKYC').length}</span>
            <span className="stat-label">KYC Incomplete</span>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="nudges-controls">
        <div className="nudges-search">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search merchants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="nudges-filter">
          <Filter size={18} />
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All Issues</option>
            <option value="inactive">Inactive</option>
            <option value="noProducts">No Products</option>
            <option value="incompleteKYC">KYC Incomplete</option>
            <option value="noPaymentGateway">No Payments</option>
            <option value="lowSales">Low Sales</option>
          </select>
          <ChevronDown size={16} />
        </div>
        <button
          className="compose-nudge-btn"
          onClick={() => openComposeModal()}
          disabled={selectedMerchants.length === 0}
        >
          <Send size={18} />
          Send Nudge ({selectedMerchants.length})
        </button>
      </div>

      {/* Merchant List */}
      <div className="nudges-table-wrapper">
        {loading ? (
          <div className="nudges-loading">
            <div className="spinner"></div>
            <p>Loading merchants...</p>
          </div>
        ) : filteredMerchants.length === 0 ? (
          <div className="nudges-empty">
            <Users size={48} />
            <h3>No merchants need attention</h3>
            <p>All merchants are active and engaged!</p>
          </div>
        ) : (
          <table className="nudges-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedMerchants.length === filteredMerchants.length && filteredMerchants.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Merchant</th>
                <th>Store</th>
                <th>Issue</th>
                <th>Last Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMerchants.map((merchant) => {
                const reason = getNudgeReason(merchant);
                return (
                  <tr key={merchant.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedMerchants.includes(merchant.id)}
                        onChange={() => handleSelectMerchant(merchant.id)}
                      />
                    </td>
                    <td>
                      <div className="mn-merchant-info">
                        <div className="mn-merchant-avatar">
                          {(merchant.firstName?.[0] || merchant.email?.[0] || '?').toUpperCase()}
                        </div>
                        <div className="mn-merchant-details">
                          <span className="mn-merchant-name">
                            {merchant.firstName && merchant.lastName
                              ? `${merchant.firstName} ${merchant.lastName}`
                              : merchant.email}
                          </span>
                          <span className="mn-merchant-email">{merchant.email}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="store-name">{merchant.storeName || 'No store'}</span>
                    </td>
                    <td>
                      <span
                        className="issue-badge"
                        style={{
                          '--badge-color': getReasonColor(reason),
                          '--badge-bg': `${getReasonColor(reason)}15`
                        }}
                      >
                        {getReasonIcon(reason)}
                        {getReasonLabel(reason)}
                      </span>
                    </td>
                    <td>
                      <span className="last-active">
                        {merchant.lastLoginAt
                          ? `${daysSince(merchant.lastLoginAt)} days ago`
                          : 'Never'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="action-btn whatsapp"
                          onClick={() => openWhatsAppForMerchant(merchant)}
                          title="Send WhatsApp"
                        >
                          <WhatsAppIcon size={16} />
                        </button>
                        <button
                          className="action-btn email"
                          onClick={() => {
                            setSelectedChannel('email');
                            openComposeModal(merchant.id);
                          }}
                          title="Send Email"
                        >
                          <Mail size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Compose Modal */}
      {showComposeModal && (
        <>
          <div className="compose-modal-overlay" onClick={() => setShowComposeModal(false)} />
          <div className="compose-modal">
            <div className="compose-modal-header">
              <h3>Send Nudge to {selectedMerchants.length} Merchant(s)</h3>
              <button className="close-btn" onClick={() => setShowComposeModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="compose-modal-body">
              {/* Channel Selection */}
              <div className="compose-section">
                <label>Send via</label>
                <div className="channel-buttons">
                  <button
                    className={`channel-btn ${selectedChannel === 'whatsapp' ? 'active' : ''}`}
                    onClick={() => setSelectedChannel('whatsapp')}
                  >
                    <WhatsAppIcon size={18} />
                    WhatsApp
                  </button>
                  <button
                    className={`channel-btn ${selectedChannel === 'email' ? 'active' : ''}`}
                    onClick={() => setSelectedChannel('email')}
                  >
                    <Mail size={18} />
                    Email
                  </button>
                </div>
              </div>

              {/* Template Type */}
              <div className="compose-section">
                <label>Message Type</label>
                <select
                  value={nudgeType}
                  onChange={(e) => {
                    setNudgeType(e.target.value);
                    const firstMerchant = merchants.find(m => m.id === selectedMerchants[0]);
                    const template = getRandomTemplate(e.target.value);
                    setCustomMessage(processTemplate(template, firstMerchant));
                  }}
                >
                  <option value="inactive">Re-engage Inactive</option>
                  <option value="noProducts">Add Products</option>
                  <option value="incompleteKYC">Complete KYC</option>
                  <option value="noPaymentGateway">Enable Payments</option>
                  <option value="lowSales">Boost Sales</option>
                </select>
              </div>

              {/* Message */}
              <div className="compose-section">
                <div className="message-header">
                  <label>Message</label>
                  <button className="regenerate-btn" onClick={regenerateMessage}>
                    <RefreshCw size={14} />
                    Generate New
                  </button>
                </div>
                <textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  rows={5}
                  placeholder="Enter your message..."
                />
                <button className="copy-btn" onClick={copyMessage}>
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? 'Copied!' : 'Copy Message'}
                </button>
              </div>

              <p className="compose-note">
                Note: Variables like {"{{merchantName}}"} will be replaced with actual values for each merchant.
              </p>
            </div>

            <div className="compose-modal-footer">
              <button className="cancel-btn" onClick={() => setShowComposeModal(false)}>
                Cancel
              </button>
              <button
                className="send-btn"
                onClick={sendNudge}
                disabled={sending || !customMessage.trim()}
              >
                {sending ? (
                  <>Sending...</>
                ) : (
                  <>
                    <Send size={16} />
                    Send Nudge
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MerchantNudges;
